import styled from 'styled-components';
import { Modal } from 'antd';

/**
 * Backdrop for the warning dialog.
 *
 * Applied as an inline style rather than through the stylesheet because a
 * global rule in GlobalStyle already reaches for `.ant-modal-mask`, and the
 * dimming here is load-bearing rather than decorative: this warning exists
 * because someone may have walked away from a screen showing payroll and
 * personal data, so the blur has to make what is behind genuinely unreadable,
 * not merely tinted. Heavier than antd's default 45% for the same reason.
 */
export const maskStyle: React.CSSProperties = {
  backgroundColor: 'rgba(12, 20, 17, 0.72)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
};

/**
 * The dialog is edge-to-edge: the countdown sits in a coloured band that runs
 * the full width of the card, so the content padding has to come off the modal
 * body and go back on inside each section.
 */
export const StyledModal = styled(Modal)`
  .ant-modal-content {
    padding: 0;
    overflow: hidden;
    border-radius: 12px;
  }

  .ant-modal-body {
    padding: 0;
  }
`;

export const Countdown = styled.div`
  padding: 24px;
  background-color: ${({ theme }) => theme.palette?.primary[700]};
`;

export const CountdownLabel = styled.div`
  margin-bottom: 4px;
  color: ${({ theme }) => theme.palette?.primary[200]};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const CountdownRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
`;

export const CountdownValue = styled.div`
  color: ${({ theme }) => theme.palette?.primary[75]};
  font-size: 44px;
  font-weight: 700;
  line-height: 1.1;
  /* Tabular figures stop the layout jittering as the digits tick over. */
  font-variant-numeric: tabular-nums;
`;

export const CountdownUnits = styled.div`
  color: ${({ theme }) => theme.palette?.primary[200]};
  font-size: 12px;
`;

export const ProgressTrack = styled.div`
  margin-top: 16px;
  height: 4px;
  background-color: rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ $fraction: number }>`
  width: ${({ $fraction }) => Math.max(0, Math.min(1, $fraction)) * 100}%;
  height: 100%;
  background-color: ${({ theme }) => theme.palette?.primary[300]};
  border-radius: inherit;
  /* Matches the one-second tick, so the bar glides instead of stepping. */
  transition: width 1s linear;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const Body = styled.div`
  padding: 24px;
  background-color: ${({ theme }) => theme.palette?.white};
`;

export const Title = styled.div`
  margin-bottom: 8px;
  color: ${({ theme }) => theme.palette?.text.primary};
  font-size: 18px;
  font-weight: 700;
`;

export const Description = styled.p`
  margin: 0 0 20px;
  color: ${({ theme }) => theme.palette?.text.disabled};
  font-size: 14px;
  line-height: 1.5;
`;
